"use strict";

const removeMarkdown = function(md) {
  md = md.replace(/<[^>]*>/g, '');
  return require('remove-markdown')(md);
}

const MAX_SUMMARY_LENGTH = 250;

const getOperationTitle = (op, method, path) => {
  return op.operationId || (method.toUpperCase() + ' ' + path);
}

const METHOD_ORDER = ["HEAD", "OPTIONS", "DELETE", "PATCH", "PUT", "POST", "GET"];

const getPathNavItem = (path, ops, tag) => {
  let pathItem = {children: [], autoselect: false, operations: path, hasOperationIds: true};
  for (let method in ops) {
    if (method === 'parameters') continue;
    let op = ops[method];
    if (tag && (!op.tags || op.tags.indexOf(tag) === -1)) continue;
    pathItem.children.push({
      title: op.operationId || method.toUpperCase(),
      fullTitle: op.operationId || (method.toUpperCase() + ' ' + path),
      operation: getOperationTitle(op, method, path),
    });
    if (!op.operationId) pathItem.hasOperationIds = false;
  }
  if (!pathItem.children.length) return null;
  pathItem.children.sort((c1, c2) => {
    let m1 = METHOD_ORDER.indexOf(c1.title);
    let m2 = METHOD_ORDER.indexOf(c2.title);
    if (m1 === m2) return 0;
    if (m1 < m2) return 1;
    if (m1 > m2) return -1;
  })
  return pathItem;
}

const initializeNavigation = module.exports = (nav, config, openapi) => {
  if (!nav) {
    if (!openapi) return null;
    if (openapi.tags) {
      nav = openapi.tags.map(t => {
        return {
          tag: t.name,
        }
      });
      nav.push({title: 'Miscellaneous', isMiscellaneous: true});
    } else {
      nav = [];
      for (let path in openapi.paths) {
        let pathItem = getPathNavItem(path, openapi.paths[path]);
        if (pathItem) nav.push(pathItem);
      }
    }
    if (openapi.info.description || openapi.externalDocs) {
      let readme = {title: 'API Description', contents: openapi.info.description || ''};
      if (openapi.externalDocs) {
        readme.contents += '\n\n### Read More\n' +
          (openapi.externalDocs.description || '') +
          '\n\n[' + openapi.externalDocs.url + '](' + openapi.externalDocs.url + ')';
      }
      nav.unshift(readme);
    }
    if (openapi.definitions) {
      nav.push({
        title: "Definitions",
        children: Object.keys(openapi.definitions).map(name => {
          let def = openapi.definitions[name];
          return {
            title: def.title || name,
            definition: name,
          }
        })
      })
    }
  }
  nav.forEach(navItem => initializeNavigationItem(navItem, config, openapi));
  if (!openapi) return nav;

  let miscItem = findMiscItem(nav);
  if (miscItem) {
    miscItem.children = [];
    for (let path in openapi.paths) {
      for (let method in openapi.paths[path]) {
        if (method === 'parameters') continue;
        let op = openapi.paths[path][method];
        let title = getOperationTitle(op, method, path);
        if (!navigationContainsOperation(nav, title)) {
          miscItem.children.push({operation: title});
        }
      }
    }
    if (!miscItem.children.length) {
      nav = nav.filter(i => !i.isMiscellaneous)
    } else {
      miscItem.children.forEach(c => initializeNavigationItem(c, config, openapi, miscItem.path));
    }
  }
  return nav;
}

const navigationContainsOperation = (items, opTitle) => {
  let container = items.filter(i => i.operation === opTitle)[0];
  if (container) return true;
  for (let i = 0; i < items.length; ++i) {
    let childContains = navigationContainsOperation(items[i].children || [], opTitle);
    if (childContains) return true;
  }
  return false;
}

const findMiscItem = (items) => {
  let miscItem = items.filter(i => i.isMiscellaneous)[0];
  if (miscItem) return miscItem;
  for (let i = 0; i < items.length; ++i) {
    let miscChild = findMiscItem(items[i].children || []);
    if (miscChild) return miscChild;
  }
}

const findOperation = (title, openapi) => {
  for (let path in openapi.paths) {
    for (let method in openapi.paths[path]) {
      let op = openapi.paths[path][method];
      if (title === method.toUpperCase() + ' ' + path) return op;
      if (op.operationId === title) return op;
    }
  }
}

const setSummaryAndTitle = (item, config, openapi) => {
  if (item.contents) {
    item.summary = item.summary || item.contents;
  } else if (item.operations) {
    item.type = 'operations';
    let defaultTitle = typeof item.operations === 'string' ? item.operations : "Operations";
    item.title = item.title || defaultTitle;
  } else if (item.definitions) {
    item.type = 'definitions';
    item.title = item.title || "Definitions";
  } else if (item.operation && openapi) {
    item.type = 'operation';
    item.title = item.title || item.operation;
    let op = findOperation(item.operation, openapi);
    if (!op) {
      item.hide = true;
    } else {
      item.summary = item.summary || op.summary || op.description;
      if (op.deprecated) {
        item.deprecated = true;
      }
    }
  } else if (item.definition && openapi) {
    item.type = 'definition';
    let def = openapi.definitions[item.definition];
    if (!def) {
      item.hide = true;
    } else {
      item.title = item.title || def.title || item.definition;
      item.summary = item.summary || def.description;
    }
  } else if (item.tag && openapi) {
    item.type = 'tag';
    item.title = item.title || item.tag;
    let tag = openapi.tags.filter(t => t.name === item.tag)[0];
    if (tag) {
      item.summary = item.contents = tag.description;
    }
  } else if (item.workflow) {
    item.type = 'workflow';
    let workflow = config.workflows[item.workflow];
    item.title = item.title || workflow.title;
    item.icon = item.icon || workflow.icon;
    item.summary = item.summary || workflow.summary;
  }
  item.title = item.title || '';
  item.summary = item.summary || '';

  item.summary = removeMarkdown(item.summary || '', {gfm: true});
  if (item.summary.length > MAX_SUMMARY_LENGTH) {
    item.summary = item.summary.substring(0, MAX_SUMMARY_LENGTH - 3) + '...';
  }
  if (!item.title) item.title = 'Untitled';
}

const populateChildren = (item, openapi) => {
  if (!openapi || item.children) return;
  if (item.operations === true) {
    item.children = Object.keys(openapi.paths).map(p => ({operations: p}))
  } else if (item.operations) {
    let pathItem = getPathNavItem(item.operations, openapi.paths[item.operations]);
    if (pathItem) {
      item.children = pathItem.children;
    }
  } else if (item.definitions) {
    item.children = Object.keys(openapi.definitions).map(d => ({definition: d}));
  } else if (item.tags) {
    item.children = openapi.tags.map(t => ({tag: t.name}));
  } else if (item.tag) {
    item.children = [];
    for (let path in openapi.paths) {
      let pathItem = getPathNavItem(path, openapi.paths[path], item.tag);
      if (pathItem) {
        if (pathItem.hasOperationIds) {
          item.children = item.children.concat(pathItem.children);
        } else {
          item.children.push(pathItem);
        }
      }
    }
    if (!item.children.length) item.hide = true;
  }
}

const initializeNavigationItem = (item, config, openapi, path='') => {
  setSummaryAndTitle(item, config, openapi);
  item.path = item.path || path + '/' + item.title.replace(/\W+/g, '_');
  populateChildren(item, openapi);
  if (item.children) item.children.forEach(c => initializeNavigationItem(c, config, openapi, item.path));
}





// WEBPACK FOOTER //
// ./src/lib/initialize-navigation.js